import Vue from 'vue';

/*
 * vue-asyncable
 *
 * (c) Savaryn Yaroslav <yariksav@gmail.com>
 *
 * Some functions was imported from nuxt.js/lib/app/utils.js
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
*/

var isFunction = function (fn) { return typeof fn === 'function'; };

var isNil = function (s) { return s === null || s === undefined; };

var isPromise = function (promise) {
  return promise && (promise instanceof Promise || typeof promise.then === 'function')
};

/*
 * vue-asyncable
 *
 * (c) Savaryn Yaroslav <yariksav@gmail.com>
 *
 * Some functions was imported from nuxt.js/lib/app/utils.js
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
*/

function promisify (fn, context) {
  var promise;
  if (isFunction(fn)) {
    promise = fn.call(this, context);
  } else {
    promise = fn;
  }

  if (!isPromise(promise)) {
    if (typeof promise === 'object') {
      return checkObjectForPromises.call(this, promise, context)
    } else {
      promise = Promise.resolve(promise);
    }
  }

  var self = this;
  return promise.then(function (data) {
    return checkObjectForPromises.call(self, data)
  })
}

function hasAsync (obj) {
  if (!obj || typeof obj !== 'object') {
    return false
  }
  for (var key in obj) {
    if (isPromise(obj[key]) || isFunction(obj[key])) { // } || isFunction(obj[key])) {
      return true
    }
  }
  return false
}

function checkObjectForPromises (obj, context) {
  var this$1 = this;
  if ( context === void 0 ) context = {};

  var promises = [];
  var self = this;
  var data = {};
  if (typeof obj !== 'object') {
    return obj
  }
  var loop = function ( key ) {
    var something = obj[key];
    // data[key] = null
    if (isFunction(something)) {
      something = something.call(this$1, context);
    }
    if (isPromise(something)) {
      something = something.then(function (res) {
        if (isNil(res)) {
          return
        }
        if (key.startsWith('...')) {
          data = Object.assign({}, data, res);
        } else {
          data[key] = res;
        }
        return res
      });
      if (isFunction(context.asyncDataError)) {
        something = something.catch(function (error) {
          return context.asyncDataError.call(self, error, { key: key, obj: obj })
        });
      }
      promises.push(something);
    } else {
      if (context.deep && hasAsync(something)) {
        promises.push(checkObjectForPromises.call(self, something, context).then(function (res) {
          data[key] = res;
        }));
      } else {
        data[key] = something;
      }
    }
  };

  for (var key in obj) loop( key );
  return Promise.all(promises).then(function () {
    return Promise.resolve(data)
  })
}

/*
 * vue-asyncable
 *
 * (c) Savaryn Yaroslav <yariksav@gmail.com>
 *
 * Some functions was imported from nuxt.js/lib/app/utils.js
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
*/

function applyComponentAsyncData (Component, asyncData) {
  var ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} };
  Component.options._originDataFn = ComponentData;

  if (!asyncData) {
    Component.options.data = ComponentData;
  } else {
    Component.options.data = function () {
      var data = ComponentData.call(this);
      return Object.assign({}, data, asyncData)
    };

    if (Component._Ctor && Component._Ctor.options) {
      Component._Ctor.options.data = Component.options.data;
    }
  }
}

function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component);
    Component._Ctor = Component;
  } else {
    Component._Ctor = Component;
    Component.extendOptions = Component.options;
  }
  // For debugging purpose
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file;
  }
  return Component
}

var hasAsyncPreload = function (options) {
  return Boolean(options.asyncData || options.fetch) // !options.__hasAsyncData && (
};

var ensureVmAsyncData = function (vm, context) {
  function hydrate (data) {
    for (var key in data) {
      vm.$set(vm, key, data[key]);
    }
    return data
  }

  var promises = [];

  if (vm.$options.asyncData && !vm.$options.__hasAsyncData) {
    promises.push(promisify.call(vm, vm.$options.asyncData, context).then(hydrate));
  }
  if (vm.$options.fetch && !vm.$options.__hasAsyncData) {
    promises.push(vm.$options.fetch(context));
  }

  // firstly we see a data
  var functionsInData = {};
  Object.keys(vm.$data).forEach(function (key) {
    var value = vm.$data[key];
    if (typeof value === 'function' || isPromise(value)) {
      vm.$set(vm, key, null);
      functionsInData[key] = value;
    }
  });
  if (Object.keys(functionsInData).length) {
    promises.push(promisify.call(vm, functionsInData, context).then(hydrate));
  }

  // vm.$options.__hasAsyncData = true
  return Promise.all(promises)
};

var ensureComponentAsyncData = function (Component, context) {
  var promises = [];

  if (Component.options.asyncData) {
    var promise = promisify(Component.options.asyncData, context);
    promise.then(function (asyncDataResult) {
      applyComponentAsyncData(Component, asyncDataResult);
      return asyncDataResult
    });
    promises.push(promise);
  }

  // Call fetch(context)
  if (Component.options.fetch) {
    promises.push(Component.options.fetch(context));
  }
  // Component.options.__hasAsyncData = true
  return Promise.all(promises)
};

var ensureAsyncData = function async (components, context) {
  if (!Array.isArray(components)) {
    if (!components) {
      return null
    } else {
      components = [components];
    }
  }

  return Promise.all(components.map(function (Component) {
    return ensureComponentAsyncData(Component, context)
  }))
};

var asyncable = {
  data: function data () {
    var data = {
      $loadingAsyncData: true
    };
    if (typeof this.$options.asyncData === 'object') {
      Object.keys(this.$options.asyncData).forEach(function (key) {
        data[key] = null;
      });
    }
    return data
  },
  created: function created () {
    this.$loadingAsyncData = true;
    var self = this;

    ensureVmAsyncData(this, this)
      .then(function () {
        self.$loadingAsyncData = false;
      }).catch(function (e) {
        self.asyncDataError(e, { type: 'load' });
        self.$loadingAsyncData = false;
      });
  },
  methods: {
    asyncDataError: function asyncDataError (e, ref) {
      var type = ref.type;
      var key = ref.key;

      // if (process.env.NODE_ENV !== 'production') {
      console.error('Async data loading error', e, type, key);
      // }
    }
  }
};

export { asyncable as Asyncable, promisify, applyComponentAsyncData, ensureAsyncData, ensureComponentAsyncData, hasAsyncPreload, sanitizeComponent, isFunction, isPromise, isNil };
